import { Link } from 'react-router-dom'

function LocationPin({ location, top, left, delay }) {
  return (
    <div
      className="absolute animate-bounce"
      style={{
        top: `${top}%`,
        left: `${left}%`,
        animationDelay: `${delay}ms`,
        animationDuration: '2s',
      }}
    >
      <div className="relative group cursor-pointer">
        <div className="w-8 h-8 bg-primary-600 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="bg-neutral-900 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap shadow-lg">
            <div className="font-semibold">{location.name}</div>
            <div className="text-neutral-300">{location.count} projects</div>
          </div>
          <div className="w-2 h-2 bg-neutral-900 rotate-45 absolute -bottom-1 left-1/2 -translate-x-1/2" />
        </div>
      </div>
    </div>
  )
}

export function MapPreview() {
  const locations = [
    { id: 1, name: 'Auckland CBD', count: 8, top: 25, left: 75 },
    { id: 2, name: 'North Shore', count: 5, top: 20, left: 78 },
    { id: 3, name: 'Wellington', count: 6, top: 70, left: 72 },
    { id: 4, name: 'Christchurch', count: 4, top: 85, left: 68 },
    { id: 5, name: 'Hamilton', count: 3, top: 38, left: 70 },
    { id: 6, name: 'Tauranga', count: 2, top: 32, left: 80 },
  ]

  return (
    <section className="py-24 bg-white">
      <div className="container-custom">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-light text-neutral-900 mb-4">
              Projects across New Zealand
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Browse developments in major cities and growing regions.
              New projects added weekly.
            </p>
          </div>

          <div className="relative bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-2xl border border-neutral-200 overflow-hidden">
            <div className="aspect-[16/10] md:aspect-[16/8] relative">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neutral-100/50 to-neutral-100" />

              <svg
                className="absolute inset-0 w-full h-full opacity-10"
                viewBox="0 0 100 100"
                preserveAspectRatio="xMidYMid slice"
              >
                <path
                  d="M 70 20 Q 75 45 72 70 Q 68 85 65 90"
                  stroke="currentColor"
                  fill="none"
                  strokeWidth="0.5"
                  className="text-neutral-900"
                />
                <path
                  d="M 75 25 L 78 30 L 80 40 L 78 55"
                  stroke="currentColor"
                  fill="none"
                  strokeWidth="0.5"
                  className="text-neutral-900"
                />
              </svg>

              {locations.map((location, index) => (
                <LocationPin
                  key={location.id}
                  location={location}
                  top={location.top}
                  left={location.left}
                  delay={index * 200}
                />
              ))}

              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-neutral-200 pointer-events-auto max-w-md mx-4">
                  <h3 className="text-2xl font-semibold text-neutral-900 mb-2">
                    28 Active Projects
                  </h3>
                  <p className="text-neutral-600 mb-6">
                    Explore developments by location, price, and features
                  </p>
                  <Link
                    to="/projects"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-semibold"
                  >
                    View All Projects
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-8">
            {locations.map((location) => (
              <Link
                key={location.id}
                to={`/projects?location=${location.name}`}
                className="p-4 bg-white border border-neutral-200 rounded-xl hover:border-primary-300 hover:shadow-md transition-all text-center group"
              >
                <div className="w-10 h-10 mx-auto mb-2 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 group-hover:bg-primary-100 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="font-semibold text-neutral-900 text-sm mb-1 group-hover:text-primary-600 transition-colors">
                  {location.name}
                </div>
                <div className="text-xs text-neutral-600">
                  {location.count} projects
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
